import axios from "axios";
import { useEffect, useState } from "react";
import { getBackendUrl } from "@src/utils/getBackendUrl";
import { getAuthHeaders } from "@src/utils/getAuthHeader";
import { PipelineHealthRunsResponse, PipelineJobType, PipelineRun } from "./pipelineHealth.types";

type Status = "loading" | "loaded" | "error";

interface UsePipelineHealthRunsParams {
	enabled: boolean;
	jobType: PipelineJobType;
	marketplace: string | null;
	from: string | null;
	to: string | null;
}

interface UsePipelineHealthRunsResult {
	runs: Array<PipelineRun>;
	total: number;
	hasMore: boolean;
	loading: boolean;
	error: boolean;
}

export const usePipelineHealthRuns = ({ enabled, jobType, marketplace, from, to }: UsePipelineHealthRunsParams): UsePipelineHealthRunsResult => {
	const hasRange = enabled && !!from && !!to;
	const targetKey = hasRange ? `${jobType}-${marketplace ?? "ALL"}-${from}-${to}` : null;

	const [runs, setRuns] = useState<Array<PipelineRun>>([]);
	const [total, setTotal] = useState(0);
	const [hasMore, setHasMore] = useState(false);
	const [status, setStatus] = useState<Status>("loaded");
	const [activeKey, setActiveKey] = useState<string | null>(null);

	if (activeKey !== targetKey) {
		setActiveKey(targetKey);
		setRuns([]);
		setTotal(0);
		setHasMore(false);
		setStatus(hasRange ? "loading" : "loaded");
	}

	useEffect(() => {
		if (!hasRange) return;

		const controller = new AbortController();
		const params = new URLSearchParams({ from: from as string, to: to as string, jobType, limit: "200" });
		if (marketplace) params.set("marketplace", marketplace);

		axios
			.get<PipelineHealthRunsResponse>(`${getBackendUrl()}/pipeline-health/runs?${params.toString()}`, {
				headers: getAuthHeaders(),
				signal: controller.signal,
			})
			.then((response) => {
				setRuns(response.data?.runs ?? []);
				setTotal(response.data?.total ?? 0);
				setHasMore(response.data?.hasMore ?? false);
				setStatus("loaded");
			})
			.catch((err) => {
				if (axios.isCancel(err)) return;
				setRuns([]);
				setTotal(0);
				setHasMore(false);
				setStatus("error");
			});

		return () => {
			controller.abort();
		};
	}, [targetKey, hasRange, jobType, marketplace, from, to]);

	return { runs, total, hasMore, loading: status === "loading", error: status === "error" };
};
